import React, {
  createContext,
  FC,
  ReactNode,
  ReactText,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Alert, Empty, Form, Spin } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { PaginationConfig } from 'antd/lib/pagination';
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import { HttpService } from '@core/services';
import { ReportsHttpService } from '@core/services/http';
import { momentizeRangePickerValues } from '@core/utils/methods';
import type { ActionCreator } from '@core/utils/store/models/interfaces';
import { Tab } from '@models/classes';
import {
  DatesAtAGlanceReportResponse,
  NewClosedProjectsReportResponse,
  OverdueTaskRow,
  OverdueTasksReportResponse,
  ProjectDurationReportResponse,
  ReportKey,
  ReportsData,
  ReportsFilters as ReportsFilterState,
  ReportsSortBy,
  StructuralPassFailReportResponse,
} from '@models/interfaces';
import { CustomTable, Tabs } from '@shared/components';
import { setCurrentTabs } from '@shared/modules/details/helpers';
import { ReportsActions, ReportsRequestLifecyclePayload } from '@store/actions';
import { getReportsFilterHash } from '@store/reducers/reports.reducer';
import { ReportsSelectors } from '@store/selectors';
import type {
  ReportsFilterFormValues,
  ReportsSummaryMetric,
} from '../../models';
import {
  DEFAULT_REPORT_FILTERS,
  didReportFiltersChange,
  getChartsItemCount,
  getGenericReportRowKey,
  getProjectTypeFilterValues,
  getProjectTypeOptions,
  getReportColumns,
  getReportDateRangeFields,
  getReportFiltersForReportChange,
  getReportGroupByOptions,
  getReportKeyFromSearch,
  getReportRowRedirectUrl,
  getReportRows,
  getReportsSortOrder,
  getReportTotal,
  getSummaryMetrics,
  getTableSorter,
  isInteractiveRowClick,
  isOptionValueAllowed,
  isStructuralPassFailReport,
  openReportRowInNewTab,
  REPORTS_REQUEST_OPTIONS,
  sanitizeReportsFiltersForReport,
} from '../../models';
import { DatesAtAGlanceReport } from '../dates-at-a-glance-report';
import { NewClosedProjectsReport } from '../new-closed-projects-report';
import { OverdueTasksReport } from '../overdue-tasks-report';
import { ProjectDurationReport } from '../project-duration-report';
import { ReportsFilters } from '../reports-filters';
import { ReportsSummary } from '../reports-summary';
import { StructuralPassFailReport } from '../structural-pass-fail-report';

import './styles.scss';

const { useForm } = Form;

interface ReportsBrowseContextValue {
  renderCurrentReport: () => ReactNode;
}

interface ReportTabContentProps {
  isActive?: boolean;
  reportKey: ReportKey;
}

const ReportsBrowseContext = createContext<ReportsBrowseContextValue | null>(
  null,
);

const getReportLifecycleAction = (
  reportKey: ReportKey,
): ActionCreator<ReportsRequestLifecyclePayload> => {
  switch (reportKey) {
    case ReportKey.DatesAtAGlance:
      return ReportsActions.getDatesAtAGlance;
    case ReportKey.NewClosedProjects:
      return ReportsActions.getNewClosedProjects;
    case ReportKey.ProjectDuration:
      return ReportsActions.getProjectDuration;
    case ReportKey.StructuralPassFail:
      return ReportsActions.getStructuralPassFail;
    case ReportKey.OverdueTasks:
    default:
      return ReportsActions.getOverdueTasks;
  }
};

export const ReportTabContent: FC<ReportTabContentProps> = ({
  isActive,
}: ReportTabContentProps) => {
  const context = useContext(ReportsBrowseContext);

  if (!isActive || !context) {
    return null;
  }

  const { renderCurrentReport } = context;

  return <>{renderCurrentReport()}</>;
};

export const ReportsBrowse: FC<RouteComponentProps> = ({
  location,
  match,
}: RouteComponentProps) => {
  const [form] = useForm();
  const [hasInvalidatedReportData, setHasInvalidatedReportData] =
    useState<boolean>(false);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const dispatch = useDispatch();
  const reportsService = useMemo(
    () => HttpService.getHttpRequests(ReportsHttpService),
    [],
  );

  const {
    data,
    errors,
    filterOptions,
    filterOptionsError,
    filterOptionsLoading,
    filters,
    loadedReports,
    loadingReportHashes,
    loadingReports,
    selectedReport,
  } = useSelector(ReportsSelectors.getReportsState);
  const currentFilterHash = useSelector(ReportsSelectors.getCurrentFilterHash);
  const reportKeyFromUrl = getReportKeyFromSearch(location.search);
  const currentReportData = data[selectedReport];
  const currentRows = getReportRows(currentReportData);
  const currentTotal = getReportTotal(currentReportData);
  const currentChartsItemCount = getChartsItemCount(currentReportData);
  const currentSummaryMetrics: ReportsSummaryMetric[] = [
    {
      label: 'Page Rows',
      value: currentRows.length,
    },
    {
      label: 'Total Rows',
      value: currentTotal,
    },
    {
      label: 'Chart Points',
      value: currentChartsItemCount,
    },
    ...getSummaryMetrics(currentReportData),
  ];
  const currentReportOptions = filterOptions?.reportOptions?.[selectedReport];
  const currentGroupByOptions = useMemo(
    () =>
      getReportGroupByOptions(
        selectedReport,
        currentReportOptions?.groupByOptions ?? [],
      ),
    [currentReportOptions, selectedReport],
  );
  const currentSortByOptions = useMemo(
    () => currentReportOptions?.sortByOptions ?? [],
    [currentReportOptions],
  );
  const projectTypeOptions = useMemo(
    () => getProjectTypeOptions(filterOptions?.projectTypes ?? []),
    [filterOptions?.projectTypes],
  );
  const currentColumns = useMemo(
    () => getReportColumns(selectedReport, filters),
    [filters, selectedReport],
  );
  const isCurrentReportLoaded =
    loadedReports[selectedReport] === currentFilterHash;
  const isCurrentReportLoading =
    !!loadingReports[selectedReport] &&
    loadingReportHashes[selectedReport] === currentFilterHash;
  const currentReportError = !!errors[selectedReport];
  const isSelectedReportSyncedWithUrl = selectedReport === reportKeyFromUrl;

  useEffect(() => {
    setCurrentTabs(location, [], setTabs);
  }, [location]);

  const loadReport = useCallback(
    async (
      reportKey: ReportKey,
      requestFilters: ReportsFilterState,
    ): Promise<ReportsData> => {
      const requestHash = getReportsFilterHash(requestFilters);
      const reportAction = getReportLifecycleAction(reportKey);
      const sanitizedFilters = sanitizeReportsFiltersForReport(
        reportKey,
        requestFilters,
        filterOptions?.reportOptions,
      );

      dispatch(reportAction.start({ requestHash }));

      try {
        let reportData: ReportsData;

        switch (reportKey) {
          case ReportKey.DatesAtAGlance:
            reportData = await reportsService.getDatesAtAGlance(
              sanitizedFilters,
              REPORTS_REQUEST_OPTIONS,
            );

            break;
          case ReportKey.NewClosedProjects:
            reportData = await reportsService.getNewClosedProjects(
              sanitizedFilters,
              REPORTS_REQUEST_OPTIONS,
            );

            break;
          case ReportKey.ProjectDuration:
            reportData = await reportsService.getProjectDuration(
              sanitizedFilters,
              REPORTS_REQUEST_OPTIONS,
            );

            break;
          case ReportKey.StructuralPassFail:
            reportData = await reportsService.getStructuralPassFail(
              sanitizedFilters,
              REPORTS_REQUEST_OPTIONS,
            );

            break;
          case ReportKey.OverdueTasks:
          default:
            reportData = await reportsService.getOverdueTasks(
              sanitizedFilters,
              REPORTS_REQUEST_OPTIONS,
            );

            break;
        }

        dispatch(reportAction.done({ data: reportData, requestHash }));

        return reportData;
      } catch (error) {
        dispatch(reportAction.fail({ requestHash }));
        throw error;
      }
    },
    [dispatch, filterOptions?.reportOptions, reportsService],
  );

  useEffect(() => {
    if (!filterOptions && !filterOptionsLoading) {
      reportsService
        .getFilterOptions(REPORTS_REQUEST_OPTIONS)
        .catch(() => undefined);
    }
  }, [filterOptions, filterOptionsLoading, reportsService]);

  useEffect(() => {
    dispatch(ReportsActions.invalidateReportsData());
    setHasInvalidatedReportData(true);
  }, [dispatch]);

  useEffect(() => {
    if (selectedReport !== reportKeyFromUrl) {
      const nextFilters = getReportFiltersForReportChange(
        reportKeyFromUrl,
        filters,
        filterOptions?.reportOptions,
      );

      if (didReportFiltersChange(filters, nextFilters)) {
        dispatch(ReportsActions.setFilters(nextFilters));
      }

      form.setFieldsValue({
        ...(isStructuralPassFailReport(reportKeyFromUrl)
          ? { dateRange: undefined }
          : {}),
        groupBy: nextFilters.groupBy,
        paceTask: nextFilters.paceTask,
        paceTaskScreen: nextFilters.paceTaskScreen,
        sortBy: nextFilters.sortBy,
        sortOrder: nextFilters.sortOrder,
      });
      dispatch(ReportsActions.setSelectedReport(reportKeyFromUrl));
    }
  }, [
    dispatch,
    filterOptions?.reportOptions,
    filters,
    form,
    reportKeyFromUrl,
    selectedReport,
  ]);

  useEffect(() => {
    if (
      hasInvalidatedReportData &&
      isSelectedReportSyncedWithUrl &&
      !isCurrentReportLoaded &&
      !isCurrentReportLoading
    ) {
      loadReport(selectedReport, filters).catch(() => undefined);
    }
  }, [
    filters,
    hasInvalidatedReportData,
    isCurrentReportLoaded,
    isCurrentReportLoading,
    isSelectedReportSyncedWithUrl,
    loadReport,
    selectedReport,
  ]);

  const onApplyFilters = async (): Promise<void> => {
    const rawDateRange = form.getFieldValue(
      'dateRange',
    ) as ReportsFilterFormValues['dateRange'];

    if (!rawDateRange?.from && !rawDateRange?.to) {
      form.setFieldsValue({ dateRange: undefined });
    }

    try {
      await form.validateFields();
    } catch {
      return;
    }

    const { dateRange, ...formFilters } =
      form.getFieldsValue() as ReportsFilterFormValues;
    const { dateRange: formattedDateRange } = momentizeRangePickerValues(
      { dateRange },
      ['dateRange'],
      true,
      true,
    );

    const dateRangeFields = getReportDateRangeFields(selectedReport);
    const dateRangeFilters = dateRangeFields
      ? {
          [dateRangeFields[0]]:
            (formattedDateRange?.from as string | undefined) || undefined,
          [dateRangeFields[1]]:
            (formattedDateRange?.to as string | undefined) || undefined,
        }
      : {};

    const nextFilters = sanitizeReportsFiltersForReport(
      selectedReport,
      {
        ...DEFAULT_REPORT_FILTERS,
        ...formFilters,
        ...dateRangeFilters,
        groupBy: isOptionValueAllowed(
          currentGroupByOptions,
          formFilters.groupBy,
        )
          ? formFilters.groupBy
          : undefined,
        page: 1,
        projectType: getProjectTypeFilterValues(
          formFilters.projectType,
          filterOptions?.projectTypes ?? [],
        ),
        size: filters.size ?? DEFAULT_REPORT_FILTERS.size,
        sortBy: isOptionValueAllowed(currentSortByOptions, formFilters.sortBy)
          ? formFilters.sortBy
          : undefined,
        sortOrder:
          formFilters.sortBy &&
          isOptionValueAllowed(currentSortByOptions, formFilters.sortBy)
            ? formFilters.sortOrder
            : undefined,
      },
      filterOptions?.reportOptions,
    );

    dispatch(ReportsActions.setFilters(nextFilters));
  };

  const onResetFilters = (): void => {
    form.resetFields();
    dispatch(
      ReportsActions.setFilters(
        sanitizeReportsFiltersForReport(
          selectedReport,
          DEFAULT_REPORT_FILTERS,
          filterOptions?.reportOptions,
        ),
      ),
    );
  };

  const onTableChange = (
    { current, pageSize }: PaginationConfig,
    _tableFilters: Record<string, ReactText[] | null>,
    sorter: SorterResult<Store> | SorterResult<Store>[],
    { action }: TableCurrentDataSource<Store>,
  ): void => {
    const sorterResult = getTableSorter(sorter);
    const nextSortOrder =
      action === 'sort'
        ? getReportsSortOrder(sorterResult?.order)
        : filters.sortOrder;
    const isSortColumnSelected =
      nextSortOrder && typeof sorterResult?.columnKey === 'string';
    let nextSortBy = filters.sortBy;

    if (action === 'sort') {
      nextSortBy = isSortColumnSelected
        ? (sorterResult.columnKey as ReportsSortBy)
        : undefined;
    }

    const nextFilters = sanitizeReportsFiltersForReport(
      selectedReport,
      {
        ...filters,
        page: current ?? filters.page ?? DEFAULT_REPORT_FILTERS.page,
        size: pageSize ?? filters.size ?? DEFAULT_REPORT_FILTERS.size,
        sortBy: isOptionValueAllowed(currentSortByOptions, nextSortBy)
          ? nextSortBy
          : undefined,
        sortOrder:
          nextSortBy && isOptionValueAllowed(currentSortByOptions, nextSortBy)
            ? nextSortOrder
            : undefined,
      },
      filterOptions?.reportOptions,
    );

    if (action === 'sort') {
      form.setFieldsValue({
        sortBy: nextFilters.sortBy,
        sortOrder: nextFilters.sortOrder,
      });
    }

    dispatch(ReportsActions.setFilters(nextFilters));
  };

  const onOverdueRowClick = useCallback(
    (record: OverdueTaskRow, event: { target?: EventTarget | null }): void => {
      const redirectUrl = getReportRowRedirectUrl(record);

      if (!redirectUrl || isInteractiveRowClick(event)) {
        return;
      }

      openReportRowInNewTab(redirectUrl);
    },
    [],
  );

  const renderCurrentReportContent = (): ReactNode => {
    if (selectedReport === ReportKey.DatesAtAGlance) {
      return (
        <DatesAtAGlanceReport
          data={currentReportData as DatesAtAGlanceReportResponse | undefined}
          filters={{
            ...filters,
            page: filters.page ?? DEFAULT_REPORT_FILTERS.page,
            size: filters.size ?? DEFAULT_REPORT_FILTERS.size,
          }}
          onTableChange={onTableChange}
          sortByOptions={currentSortByOptions}
        />
      );
    }

    if (selectedReport === ReportKey.OverdueTasks) {
      return (
        <OverdueTasksReport
          data={currentReportData as OverdueTasksReportResponse | undefined}
          filters={{
            ...filters,
            page: filters.page ?? DEFAULT_REPORT_FILTERS.page,
            size: filters.size ?? DEFAULT_REPORT_FILTERS.size,
          }}
          onTableChange={onTableChange}
          onRowClick={onOverdueRowClick}
          sortByOptions={currentSortByOptions}
        />
      );
    }

    if (selectedReport === ReportKey.NewClosedProjects) {
      return (
        <NewClosedProjectsReport
          data={
            currentReportData as NewClosedProjectsReportResponse | undefined
          }
          filters={{
            ...filters,
            page: filters.page ?? DEFAULT_REPORT_FILTERS.page,
            size: filters.size ?? DEFAULT_REPORT_FILTERS.size,
          }}
          onTableChange={onTableChange}
          sortByOptions={currentSortByOptions}
        />
      );
    }

    if (selectedReport === ReportKey.ProjectDuration) {
      return (
        <ProjectDurationReport
          data={currentReportData as ProjectDurationReportResponse | undefined}
          filters={{
            ...filters,
            page: filters.page ?? DEFAULT_REPORT_FILTERS.page,
            size: filters.size ?? DEFAULT_REPORT_FILTERS.size,
          }}
          groupByOptions={currentGroupByOptions}
          onTableChange={onTableChange}
          sortByOptions={currentSortByOptions}
        />
      );
    }

    if (selectedReport === ReportKey.StructuralPassFail) {
      return (
        <StructuralPassFailReport
          data={
            currentReportData as StructuralPassFailReportResponse | undefined
          }
          filters={{
            ...filters,
            page: filters.page ?? DEFAULT_REPORT_FILTERS.page,
            size: filters.size ?? DEFAULT_REPORT_FILTERS.size,
          }}
          onTableChange={onTableChange}
          sortByOptions={currentSortByOptions}
        />
      );
    }

    if (currentRows.length || currentReportData) {
      return (
        <>
          <ReportsSummary metrics={currentSummaryMetrics} />
          <CustomTable
            columns={currentColumns}
            dataSource={currentRows}
            onChange={onTableChange}
            pagination={{
              current: filters.page ?? DEFAULT_REPORT_FILTERS.page,
              pageSize: filters.size ?? DEFAULT_REPORT_FILTERS.size,
              total: currentTotal,
            }}
            rowKey={getGenericReportRowKey}
          />
        </>
      );
    }

    return (
      <Empty
        className="prov-reports-page__empty"
        description="No report data"
      />
    );
  };

  const renderCurrentReport = (): ReactNode => (
    <>
      {currentReportError && (
        <Alert
          className="prov-reports-page__alert"
          message="Unable to load selected report"
          type="error"
        />
      )}
      {renderCurrentReportContent()}
    </>
  );
  const reportsBrowseContextValue = useMemo(
    () => ({ renderCurrentReport }),
    [renderCurrentReport],
  );

  return (
    <div className="prov-reports-page">
      <header className="prov-reports-page__header">
        <h1>Reports</h1>
      </header>

      <ReportsFilters
        currentGroupByOptions={currentGroupByOptions}
        currentSortByOptions={currentSortByOptions}
        filterOptions={filterOptions}
        filterOptionsError={filterOptionsError}
        filterOptionsLoading={filterOptionsLoading}
        form={form}
        onApplyFilters={onApplyFilters}
        onResetFilters={onResetFilters}
        projectTypeOptions={projectTypeOptions}
        selectedReport={selectedReport}
      />

      <section className="prov-reports-page__content">
        <Spin spinning={isCurrentReportLoading}>
          <ReportsBrowseContext.Provider value={reportsBrowseContextValue}>
            {tabs.length ? (
              <Tabs
                tabs={tabs}
                params={match.params}
                showProjectFuzeIDs={false}
              />
            ) : null}
          </ReportsBrowseContext.Provider>
        </Spin>
      </section>
    </div>
  );
};
